import { useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Input } from '@antimatterhq/ui/components/input';
import { Label } from '@antimatterhq/ui/components/label';
import { useOnKeyDown } from '@/utils/util';
import { FormMessage } from '@/components/form/form';

export type ObservableRelationshipDialogProps = {
  onSave: () => void;
  relationship?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setRelationship: (relationship: string) => void;
  isEdit: boolean;
  existingRelationships: string[];
};

export const ObservableRelationshipDialog = ({
  onSave,
  relationship,
  open,
  onOpenChange,
  setRelationship,
  isEdit,
  existingRelationships,
}: ObservableRelationshipDialogProps) => {
  const { handleKeyDown } = useOnKeyDown({
    enabled: !!relationship,
    onEnter: onSave,
  });

  const [error, setError] = useState<string | undefined>(undefined);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setRelationship(value);
    const isDuplicate = existingRelationships.some(
      (r) => r.toLowerCase() === value.toLowerCase(),
    );

    if (isDuplicate) {
      setError(`Relationship "${value}" already exists`);
      return;
    }

    setError(undefined);
  };

  const handleOpenChange = (openState: boolean) => {
    setError(undefined);
    onOpenChange(openState);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit && 'Edit observable relationship'}
            {!isEdit && 'Add observable relationship'}
          </DialogTitle>
        </DialogHeader>
        <div>
          <Label htmlFor="relationship" id="relationship-label">
            Observable relationship*
          </Label>
          <Input
            id="relationship"
            name="relationship"
            value={relationship}
            onChange={handleChange}
            autoComplete="off"
            onKeyDown={handleKeyDown}
            aria-labelledby="relationship-label"
            data-testid="relationship-input"
          />
          {error && (
            <FormMessage type="error" className="mt-2">
              {error}
            </FormMessage>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={onSave}
            variant="primary"
            disabled={!relationship || !!error}
            data-testid="relationship-save-button"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
